import { FormattedMessage, useIntl } from "react-intl";
import { Tooltip } from "@/components/common/Tooltip";
import {
  POWER_GROUPS,
  POWERS_WEIGHT_MAX,
  POWER_WEIGHT_LEVEL_MESSAGES,
  powerGroupLabelId,
  powerWeightLevel,
} from "@/components/match/match-design-rules";
import type { PowerGroupKey } from "@/components/match/match-result-codec";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";

interface ProfileSilhouetteProps {
  // CreativityElement-keyed Importance values, wire scale 0..1000.
  elementWeights: Record<string, number>;
  showLabels?: boolean;
  // Render only this subset of clusters (in the order given). Defaults to the
  // full canonical row of six. Used by PowerProfileEditor to split the grid
  // into two rows of three so an inline editor can sit between them.
  groups?: ReadonlyArray<(typeof POWER_GROUPS)[number]>;
  // Interactive mode: the cluster label becomes a button that toggles the
  // cluster's detail strip, and every bar becomes its own button.
  onClusterClick?: (key: PowerGroupKey) => void;
  // Optional per-bar click (interactive mode): opens the cluster without
  // toggling it closed, unlike the label which toggles (spark #173). Falls
  // back to onClusterClick when absent.
  onBarClick?: (key: PowerGroupKey) => void;
  // Visually emphasises the matching cluster cell (paired with onClusterClick).
  activeCluster?: PowerGroupKey | null;
  className?: string;
}

// 30 muted vertical bars across 6 cluster cells, laid out as a 3-column CSS
// grid so the bar-blocks line up vertically between the two rows regardless
// of label length. Every bar carries a tooltip naming its power and value;
// in interactive mode bars and label are individual buttons (no nested
// interactive elements). See @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1 and CONTEXT.md [[Profile silhouette]].
export function ProfileSilhouette({
  elementWeights,
  showLabels = true,
  groups = POWER_GROUPS,
  onClusterClick,
  onBarClick,
  activeCluster = null,
  className = "",
}: ProfileSilhouetteProps) {
  const intl = useIntl();
  const interactive = Boolean(onClusterClick);
  return (
    <div className={`grid grid-cols-3 gap-3 ${className}`}>
      {groups.map((group) => {
        const elements = group.abbrs
          .map((abbr) => {
            const element = Object.entries(ELEMENT_CODES).find(([, a]) => a === abbr)?.[0];
            return { abbr, element };
          })
          .filter((x): x is { abbr: string; element: string } => Boolean(x.element));
        const isActive = activeCluster === group.key;
        // The active cell keeps a subtle fill so the expanded strip below
        // stays visually anchored to its cluster after the open animation
        // has finished (spark #173).
        const cellClass = interactive
          ? `group flex flex-col items-center gap-1 rounded-2xl px-3 py-2 transition ${
              isActive ? "bg-ink/[0.06]" : ""
            }`
          : "flex flex-col items-center gap-1";
        // Sentence case, no uppercase: the label toggles a same-page strip,
        // and caps are reserved for cross-page navigation (@adr SPARK-0026-all-caps-reserved-for-cross-page-navigation@1).
        const labelClass = interactive
          ? `cursor-pointer rounded text-center text-[11px] leading-tight transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
              isActive ? "text-ink" : "text-ink/45 group-hover:text-ink/80"
            }`
          : "text-center text-[11px] leading-tight text-ink/45";
        const barColor = interactive
          ? isActive
            ? "bg-ink/80"
            : "bg-ink/40 group-hover:bg-ink/70"
          : "bg-ink/40 hover:bg-ink/80";
        const bars = (
          <div className="-mx-1 flex h-10 items-end">
            {elements.map(({ abbr, element }) => {
              // The wire-layer mapper (`match-design-mapping.ts`) densifies
              // `elementWeights` at the read boundary, so every CreativityElement
              // key is present. The `?? 0` is a defensive type-narrowing
              // fallback for the unchecked-indexed-access lookup, not a
              // semantic default; if it ever fires, the boundary is broken.
              const w = elementWeights[element] ?? 0;
              const pct = Math.max(4, (w / POWERS_WEIGHT_MAX) * 100);
              const name = POWER_BY_ABBR[abbr]?.name ?? abbr;
              // Tooltip speaks the same four-level language as the tiles
              // instead of the raw stored 0..1000 value.
              const levelLabel = intl.formatMessage(
                POWER_WEIGHT_LEVEL_MESSAGES[powerWeightLevel(w)],
              );
              const bar = (
                <span className="flex h-10 w-6 items-end justify-center">
                  <span
                    className={`block w-2.5 transition ${barColor}`}
                    style={{ height: `${pct}%` }}
                  />
                </span>
              );
              return (
                <Tooltip key={abbr} content={`${name} · ${levelLabel}`}>
                  {interactive ? (
                    <button
                      type="button"
                      aria-label={`${name} · ${levelLabel}`}
                      onClick={() => (onBarClick ?? onClusterClick)?.(group.key)}
                      className="cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                    >
                      {bar}
                    </button>
                  ) : (
                    bar
                  )}
                </Tooltip>
              );
            })}
          </div>
        );
        const label = <FormattedMessage id={powerGroupLabelId(group.key)} />;
        return (
          <div key={group.key} className={cellClass}>
            {bars}
            {showLabels ? (
              interactive ? (
                <button
                  type="button"
                  onClick={() => onClusterClick?.(group.key)}
                  aria-expanded={isActive}
                  className={labelClass}
                >
                  {label}
                </button>
              ) : (
                <span className={labelClass}>{label}</span>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
